import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBranding, logoUrl } from "@/hooks/useBranding";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";

const SLOTS = [
  { key: "client", label: "Client Logo", required: false, hint: "Your client's brand mark (optional)." },
  { key: "partner", label: "Ascend Solution Logo", required: false, hint: "Partner/solution provider logo (optional)." },
];

function LogoSlot({ slot, branding, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const present = branding[slot.key];

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Logo must be under 3MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/branding/logo/${slot.key}`, fd);
      toast.success(`${slot.label} updated — now visible across the app.`);
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function onRemove() {
    setBusy(true);
    try {
      await api.delete(`/branding/logo/${slot.key}`);
      toast.success(`${slot.label} removed.`);
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="hah-card p-5 flex flex-col" data-testid={`branding-slot-${slot.key}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-[#1F2937]">{slot.label}</h3>
        {slot.required && <span className="text-[10px] font-bold uppercase tracking-wider text-[#B45309] bg-[#FEF3C7] px-2 py-0.5 rounded">Required</span>}
      </div>
      <p className="text-xs text-[#6B7280] mb-4">{slot.hint}</p>

      <div className="flex-1 flex items-center justify-center rounded-lg border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] min-h-[140px] p-4 mb-4">
        {present ? (
          <img src={logoUrl(slot.key, branding.version)} alt={slot.label}
               className="max-h-24 max-w-full object-contain" data-testid={`branding-preview-${slot.key}`} />
        ) : (
          <div className="flex flex-col items-center text-[#9CA3AF]">
            <ImageIcon className="w-8 h-8 mb-2" />
            <span className="text-xs">No logo uploaded</span>
          </div>
        )}
      </div>

      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
             className="hidden" onChange={onFile} data-testid={`branding-input-${slot.key}`} />
      <div className="flex gap-2">
        <Button onClick={() => inputRef.current?.click()} disabled={busy}
                className="flex-1 bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`branding-upload-${slot.key}`}>
          <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : present ? "Replace" : "Upload"}
        </Button>
        {present && (
          <Button onClick={onRemove} disabled={busy} variant="outline"
                  className="border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2]" data-testid={`branding-remove-${slot.key}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function BrandingSettings() {
  const { branding, refresh } = useBranding();

  return (
    <div className="space-y-6" data-testid="branding-page">
      <PageHeader title="Branding" subtitle="Upload client logos shown across the app — in the sidebar and on the login screen. The Hospital at Home brand is always preserved." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
        {SLOTS.map((s) => (
          <LogoSlot key={s.key} slot={s} branding={branding} onChanged={refresh} />
        ))}
      </div>

      <div className="hah-card p-5 max-w-3xl">
        <h3 className="text-sm font-semibold text-[#1F2937] mb-3">Preview — how it appears</h3>
        <div className="rounded-lg p-4 flex items-center gap-3" style={{ background: "linear-gradient(165deg, #0B7E8C 0%, #095e69 100%)" }}>
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold">H@H</span>
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm leading-tight">Hospital at Home</div>
            <div className="text-white/70 text-[10px] uppercase tracking-wider">Care Beyond Walls</div>
          </div>
          <div className="flex items-center gap-2.5 ml-2">
            {branding.client && <div className="bg-white rounded-xl px-3 py-2 shadow-sm ring-1 ring-black/5"><img src={logoUrl("client", branding.version)} alt="client" className="h-9 w-auto max-w-[130px] object-contain" /></div>}
            {branding.partner && <div className="bg-white rounded-xl px-3 py-2 shadow-sm ring-1 ring-black/5"><img src={logoUrl("partner", branding.version)} alt="partner" className="h-9 w-auto max-w-[130px] object-contain" /></div>}
          </div>
        </div>
        <p className="text-xs text-[#6B7280] mt-3">Recommended: transparent PNG or SVG, wide/landscape, under 3MB.</p>
      </div>
    </div>
  );
}
